import React, { type MouseEvent } from 'react';

import Button, { type ButtonProps } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { SwitcherTab } from './types';

const styles = cssMap({
	switcherOptionsContainer: {
		textDecoration: 'none',
		borderRadius: token('radius.small'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('elevation.surface.raised'),
		height: '32px',
		paddingInline: token('space.050'),
	},
	switcherOption: {
		flexGrow: 1,
	},
});

const tabButtonProps: Pick<ButtonProps, 'appearance' | 'spacing' | 'shouldFitContainer'> = {
	appearance: 'subtle',
	spacing: 'compact',
	shouldFitContainer: true,
};

interface SwitcherProps<T extends string> {
	selectedTab?: T;
	tabs: SwitcherTab[];
	setTab: (tabKey: T) => void;
	testId?: string;
}

export const Switcher = <T extends string>({
	selectedTab,
	tabs,
	setTab,
	testId,
}: SwitcherProps<T>): React.JSX.Element => {
	const switchToTab = (event: MouseEvent, tabKey: string) => {
		event.preventDefault();
		setTab(tabKey as T);
	};

	return (
		<Inline alignBlock="center" xcss={styles.switcherOptionsContainer} testId={testId}>
			{tabs.map((tab, index) => (
				<Box key={tab.key} xcss={styles.switcherOption}>
					<Button
						{...tabButtonProps}
						iconBefore={tab.icon}
						isSelected={selectedTab === tab.key}
						onClick={(e) => switchToTab(e, tab.key)}
						testId={`${testId}-${index}-button`}
					>
						{tab.label}
					</Button>
				</Box>
			))}
		</Inline>
	);
};
